import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "@phosphor-icons/react";
import { Check, X, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { PasswordStrength } from "@/components/PasswordStrength";
import { scorePassword } from "@/lib/authHelpers";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [busy, setBusy] = useState(false);

  const pwOk = scorePassword(pw).valid;
  const pwMatch = pw.length > 0 && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (!pwOk) return toast.error("Password must be 8+ characters with a letter and a number");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/40 p-6" data-testid="reset-password-page">
      <form onSubmit={submit} className="w-full max-w-sm border border-border bg-card p-8">
        <ShieldCheck size={32} weight="fill" className="text-primary" />
        <h1 className="text-2xl font-bold mt-3">Create new password</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-5">Choose a strong password to secure your RecoverOS account.</p>
        {!token && <p className="text-sm text-destructive mb-3">Missing or invalid reset link.</p>}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Create New Password</Label>
            <div className="relative">
              <Input type={showPw ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-sm pr-10" data-testid="reset-new-password" />
              <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1} aria-label={showPw ? "Hide password" : "Show password"} className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground" data-testid="reset-new-password-toggle">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <PasswordStrength password={pw} />
          </div>
          <div>
            <Label className="text-xs">Confirm New Password</Label>
            <div className="relative">
              <Input type={showPw2 ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} className="rounded-sm pr-10" data-testid="reset-confirm-password" />
              <button type="button" onClick={() => setShowPw2((v) => !v)} tabIndex={-1} aria-label={showPw2 ? "Hide password" : "Show password"} className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground" data-testid="reset-confirm-password-toggle">
                {showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {pw2.length > 0 && (
              <p className={`mt-1 flex items-center gap-1.5 text-xs ${pwMatch ? "text-emerald-600" : "text-rose-600"}`} data-testid="reset-pw-match">
                {pwMatch ? <><Check className="h-3.5 w-3.5" /> Passwords match</> : <><X className="h-3.5 w-3.5" /> Passwords do not match</>}
              </p>
            )}
          </div>
          <Button type="submit" disabled={busy || !token || !pwOk || !pwMatch} className="w-full rounded-sm" data-testid="reset-submit">{busy ? "Saving…" : "Reset Password"}</Button>
        </div>
      </form>
    </div>
  );
}
